import { Box, FormControl, IconButton, InputLabel, TextField, useTheme } from "@mui/material";
import AsteriskSolid from "../../../assets/faIcons/AsteriskSolid";
import DeleteIcon from "@mui/icons-material/Delete";
import MuiSVGIcon from "../../IconPicker/MuiSVGIcon";

export default function SimpleTextField(props) {
  const {
    autoComplete,
    autoFocus,
    classes,
    color,
    customIcon = <></>,
    defaultValue,
    disabled = false,
    error = false,
    errorText = "Required Field",
    externalLabel = true,
    FormHelperTextProps,
    fullWidth = true,
    helperText,
    id,
    InputLabelProps = {},
    InputProps = {},
    inputProps,
    label = "",
    labelPlacement = "top",
    labelStyles = {},
    margin,
    multiline = false,
    name,
    onChange,
    onClick = () => { },
    onDeleteIconClick = () => { },
    readOnly = false,
    required = false,
    rows,

    // The Custom icon sits next to the label
    // Should porbably replace the showDeleteIcon props with this at some point
    showCustomIcon = false,

    //Delete Icon Boolean
    showDeleteIcon = false,
    sx = {},
    size = "small",
    type = "",

    value,
    variant = "outlined",
  } = props;
  const theme = useTheme()
  const { palette = {} } = theme
  const { typography = {}, success = {}, background = {}, text = {}, divider, mode } = palette
  const isDark = mode === "dark";
  const formattedLabel = label?.replace(/\s+/g, "-");
  
  // Brand colors
  const greenColor = success?.main || "#A0C46D";
  const greenDark = "#7a9c4a";
  const bgPaper = background?.paper || (isDark ? "#032d42" : "#ffffff");
  const bgDefault = background?.default || (isDark ? "#021e2d" : "#f8fafc");
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.3)" : "#e2e8f0");

  let labelWithAstrisk;
  if (required) {
    labelWithAstrisk = (
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {label}
        <MuiSVGIcon
          icon={<AsteriskSolid />}
          sx={{
            color: isDark ? "#f87171" : "#ef4444",
            fontSize: "8px",
          }}
        />
      </Box>
    );
  }

  // Default styling with brand colors
  const defaultSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      transition: "all 0.2s ease-in-out",
      backgroundColor: bgPaper,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: greenColor,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: greenColor,
        borderWidth: "2px",
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px rgba(160, 196, 109, ${isDark ? '0.15' : '0.1'})`,
      },
      "&.Mui-disabled": {
        backgroundColor: isDark ? "rgba(148, 163, 184, 0.05)" : "#f8fafc",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      fontSize: "0.875rem",
      "&::placeholder": {
        color: textSecondary,
        opacity: 1,
      },
    },
    "& .MuiInputLabel-root": {
      color: textSecondary,
      "&.Mui-focused": {
        color: greenColor,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: "4px",
      marginTop: "6px",
      fontSize: "0.75rem",
      "&.Mui-error": {
        color: isDark ? "#f87171" : "#ef4444",
      },
    },
    ...sx,
  };

  return (
    <Box
      sx={
        labelPlacement === "start"
          ? {
            display: "flex",
            alignItems: "center",
          }
          : {}
      }
    >
      {externalLabel ? (
        <Box
          sx={{
            color: textSecondary,
            display: "flex",
            fontFamily: "Lato, sans-serif",
            fontWeight: "600",
            justifyContent: "space-between",
            lineHeight: 1,
            marginBottom: `${labelPlacement === "start" ? "inherit" : "8px"}`,
            marginTop: `${labelPlacement === "start" ? "inherit" : "4px"}`,
            marginRight: `${labelPlacement === "start" ? "12px" : ""}`,
            ...labelStyles,
          }}
        >
          <InputLabel
            id={formattedLabel}
            sx={{
              color: textSecondary,
              fontFamily: "Lato, sans-serif",
              fontWeight: 600,
              fontSize: "0.875rem",
              position: "relative",
              transform: "none",
            }}
          >
            {required ? labelWithAstrisk : label}
          </InputLabel>
          {showDeleteIcon ? (
            <IconButton
              aria-label={`Delete ${label}`}
              onClick={() => onDeleteIconClick()}
              sx={{
                padding: "4px",
                color: textSecondary,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  color: isDark ? "#f87171" : "#ef4444",
                  backgroundColor: isDark ? "rgba(248, 113, 113, 0.1)" : "rgba(239, 68, 68, 0.1)",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          ) : null}
          {showCustomIcon ? customIcon : null}
        </Box>
      ) : null}
      <FormControl
        className="simple-select"
        error={error}
        size={size}
        sx={{ width: "100%" }}
        variant={variant ? variant : "standard"}
      >
        <TextField
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          classes={classes}
          color={color}
          defaultValue={defaultValue}
          disabled={disabled}
          error={error}
          FormHelperTextProps={FormHelperTextProps}
          fullWidth={fullWidth}
          helperText={error ? errorText : helperText}
          label={externalLabel ? null : label}
          id={id}
          InputLabelProps={{
            required: false,
            //if externalLabel is true, set shrink to false
            shrink: externalLabel ? false : undefined,
            ...InputLabelProps,
            style: {
              ...InputLabelProps?.style,
              //if externalLabel is true, make it visible and hide this one. Do that by setting fontSize to 0 and color to transparent
              fontSize: externalLabel ? 0 : "inherit",
              color: externalLabel ? "transparent" : "inherit",
            },
          }}
          inputProps={inputProps}
          InputProps={{
            ...InputProps,
            readOnly,
          }}
          margin={margin}
          multiline={multiline}
          name={name}
          onChange={(event) => {
            onChange(event);
          }}
          onClick={() => onClick()}
          required={required}
          rows={rows}
          type={type}
          size={size}
          sx={defaultSx}
          value={value}
          variant={variant}
        />
      </FormControl>
    </Box>
  );
}
